import { PureComponent, Fragment } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import Popover from '../../../Popover'

const RADIAN = Math.PI / 180
const PIE_LABELS_MAX_SIZE = 3
const PIE_LABEL_LINES_MAX_SIZE = 1
const SINGLE_LINE_MAX_SIZE = 6
const CHAR_SIZE = 16

class PieLabel extends PureComponent {
  static propTypes = {
    topPies: PropTypes.array.isRequired
  }

  render () {
    const {
      cx,
      cy,
      midAngle,
      outerRadius,
      percent,
      pieKey = 'name',
      topPies,
      fill,
      lineSize = SINGLE_LINE_MAX_SIZE,
      formatPercent,
      checkIsVisible
    } = this.props
    const name = _.get(this.props, pieKey, '')
    const isPieExist = _.flow(
      // 限制只取前三筆
      topPies => _.slice(topPies, 0, PIE_LABELS_MAX_SIZE),
      topThree => !!_.find(topThree, { [pieKey]: name })
    )(topPies)
    const formatedPercent = formatPercent
      ? formatPercent(percent)
      : percent
    const percentText = `${formatedPercent}%`
    const isLabelVisible = checkIsVisible
      ? checkIsVisible({ ...this.props, percent: formatedPercent })
      : true
    // 判斷此圓餠名稱是否顯示
    if (
      !isLabelVisible ||
      (!_.isEmpty(topPies) && !_.find(topPies, { [pieKey]: name }))
    ) {
      return null
    }
    if (!isPieExist) {
      return null
    }
    // 每一行最多 lineSize 個字
    const [isEllipsisName, nameChunks] = _.flow(
      name => _.chunk(name, lineSize),
      chunkedNames => {
        const isEllipsisName = _.size(chunkedNames) > PIE_LABEL_LINES_MAX_SIZE
        let nameChunks = chunkedNames
        if (isEllipsisName) {
          nameChunks = _.flow(
            nameChunks => _.slice(nameChunks, 0, PIE_LABEL_LINES_MAX_SIZE),
            topNameChunks => _.map(topNameChunks, (chunk, index) => {
              const chunkSize = _.size(chunk)
              if (index === _.size(topNameChunks) - 1) {
                return [...chunk.slice(0, chunkSize - 1), '...']
              }
              return chunk
            })
          )(nameChunks)
        }
        return [isEllipsisName, nameChunks]
      }
    )(name)
    const nameChunksSize = _.size(nameChunks)
    const cos = Math.cos(-RADIAN * midAngle)
    const sin = Math.sin(-RADIAN * midAngle)
    // 以角度判斷圓餅名稱底線的方向
    const xaxisDirection = cos >= 0 ? 1 : -1
    const yaxisDirection = sin >= 0 ? 1 : -1
    // 圓餅與底線之間底的 X 軸距離
    const mx = (
      cx +
      (outerRadius + 24) *
      // 強制 label 延伸線的 x 軸底線有一定角度
      _.max([Math.abs(cos), 0.15]) * xaxisDirection
    )
    const my = (
      cy +
      (
        outerRadius + (
          yaxisDirection > 0
            ? 35 * nameChunksSize
            : 35 / nameChunksSize
        )
      ) *
      // 強制 label 延伸線的 y 軸底線有一定角度
      _.max([Math.abs(sin), 0.3]) * yaxisDirection
    )
    const ex = mx + xaxisDirection * CHAR_SIZE
    const ey = my

    // 連接著圓餅與線的第一個座標
    const startPoint = `${cx + (outerRadius * cos)},${cy + (outerRadius * sin)}`
    // startPoint 到圓餅名稱底下的座標
    const firstTurnPoint = `${mx},${my}`
    // 計算 label 延伸線的長度
    const longestTextSize = _.flow(
      nameChunks => _.map(nameChunks, nameChunk => {
        const word = nameChunk.join('')
        // 分成中文與其他單字計算底線延伸的長度
        const chineseChars = word.match(/[\u4E00-\u9FFF]/gi) || []
        const chineseWord = chineseChars.join('')
        const otherWord = _.replace(word, new RegExp(`(${chineseChars.join('|')})`, 'g'), '')
        return {
          // 中文多算一個字延伸出去的線比較好看
          wordSize: (
            _.size(otherWord) * (CHAR_SIZE / 2) +
            (_.size(chineseWord) + 1) * CHAR_SIZE
          ),
          otherWord,
          chineseWord
        }
      }),
      // 排序切分多列的圓餠名稱找出最長的來計算底線長度
      chunksWithSize => _.orderBy(chunksWithSize, ['wordSize'], 'desc'),
      sortedChunksByWordSize => _.get(sortedChunksByWordSize, '0.wordSize', 0)
    )([...nameChunks, _.split(percentText, '')])
    const endWordsLineXPoint = ex + (xaxisDirection * longestTextSize)
    // 圓餅名稱底線終點的座標
    const endPoint = `${endWordsLineXPoint},${ey}`
    const lineSection = (
      <path
        d={`M${startPoint}L${firstTurnPoint}L${endPoint}`}
        stroke='#979ba7'
        fill='none'
      />
    )

    // 固定顯示在圖表外的文字靠左對齊
    const textAnchor = 'start'
    // 文字內縮 1.5 個字的距離
    const textXaxis = (
      xaxisDirection > 0
        ? mx
        : endWordsLineXPoint
    ) + CHAR_SIZE * 1.5

    const nameSection = _.map(
      nameChunks,
      (chunk, idx) => {
        // idx * 16 每多一個換行 y 軸需要多一個字 + 字上間距的高度
        const y = (ey - nameChunksSize * 20) + (idx * CHAR_SIZE) - (nameChunksSize === 1 ? 4 : 0)
        const iconX = xaxisDirection > 0 ? ex - 10 : textXaxis - CHAR_SIZE
        const iconY = y - 10
        return (
          <Fragment key={idx}>
            {
              // 有可能 reason 被切分成多個，只有第一個要顯示 icon
              idx === 0 && (
                <svg
                  x={iconX}
                  y={iconY}
                  width={8}
                  height={8}
                >
                  <circle
                    cx='4'
                    cy='4'
                    r='4'
                    fill={fill}
                  />
                </svg>
              )}
            <text
              x={textXaxis}
              y={y}
              fontSize={14}
              textAnchor={textAnchor}
              fill='#464e56'
            >
              {chunk.join('')}
            </text>
          </Fragment>
        )
      }
    )

    return (
      <Popover
        content={name}
        hide={!isEllipsisName}
      >
        <g>
          {lineSection}
          {nameSection}
          <text
            x={textXaxis}
            y={ey - 24}
            dy={18}
            textAnchor={textAnchor}
            fill='#979ba7'
            fontSize={12}
          >
            {percentText}
          </text>
        </g>
      </Popover>
    )
  }
}

export default PieLabel
